// 🔏 SELO VISÍVEL DE ASSINATURA DIGITAL (ICP-Brasil) — B212 F4 (AI_126)
// ─────────────────────────────────────────────────────────────
// Bloco VISUAL impresso no PDF assinado (como a Memed faz). NÃO é a assinatura
// criptográfica em si — essa é o campo PAdES invisível embutido no arquivo. Este
// selo é a REPRESENTAÇÃO VISÍVEL para quem lê o documento (farmácia, paciente,
// perito): mostra quem assinou, registro, data e que é assinatura ICP-Brasil.
//
// Entra SÓ no PDF assinado (a impressão comum continua com o rodapé normal).
// Renderiza igual na impressão (window.print) e no render headless (gerar-pdf):
// é SVG + texto inline, sem JavaScript nem imagem externa.
// ─────────────────────────────────────────────────────────────

import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { QRCodeSVG } from "qrcode.react";
import {
  urlValidacao,
  formatarCodigoExibicao,
} from "../../validacoes/services/validacaoCliente";

function esc(s: string): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// B340 (AI_150) — LADO do QR dentro do selo COMPACTO, em CSS px.
// 1 px CSS = 1/96" = 0,2646 mm na impressão → 57 px ≈ 15,1 mm. Com a quiet zone
// de 4 módulos, o símbolo (versão 3, 29 módulos) fica com ~0,41 mm por módulo —
// acima do piso prático de 0,4 mm para leitor de balcão de farmácia. NÃO reduzir
// sem refazer a conta: abaixo disso a farmácia passa a falhar na leitura.
const QR_COMPACTO_PX = 57;

/** SVG do QR de validação (estático — o render do PDF roda com JS desligado). */
function qrSvg(codigo: string, tamanho: number): string {
  return renderToStaticMarkup(
    React.createElement(QRCodeSVG, {
      value: urlValidacao(codigo),
      size: tamanho,
      level: "M",
      // Quiet zone de 4 módulos (padrão QR) DENTRO do SVG: sem ela muitos
      // leitores de farmácia falham, e aqui o entorno é a borda da célula.
      marginSize: 4,
      bgColor: "#ffffff",
      fgColor: "#1a1a1a",
    })
  );
}

// Chave (indicador de assinatura digital ICP-Brasil).
const chaveSvg = (tam: number): string =>
  `<svg width="${tam}" height="${tam}" viewBox="0 0 24 24" fill="none" stroke="#1a3a6e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="15" r="4"/><line x1="10.85" y1="12.15" x2="19.5" y2="3.5"/><line x1="18" y1="5" x2="21" y2="8"/><line x1="15" y1="8" x2="18" y2="11"/></svg>`;

export interface DadosSelo {
  /** Nome do signatário (ex.: "LEONARDO TOLEDO MOTA"). */
  nome: string;
  /** Registro profissional (ex.: "CRM 3793 RO"). */
  registro: string;
  /** Data/hora da assinatura (ex.: "26/06/2026 21:41"). */
  data: string;
  /** Versão pequena p/ caber numa célula (campos 17/23 da LME). */
  compacto?: boolean;
  /**
   * B340 (AI_150) — código de validação do DOCUMENTO. Quando presente, o selo
   * COMPACTO ganha o QR ao lado do carimbo (pedido da farmácia de alto custo
   * para os campos 17 e 23 da LME; estendido às demais fichas do SUS).
   *
   * ⚠️ É o código DAQUELE documento. No caso misto (B306) cada documento tem o
   * seu — um selo compartilhado levaria a farmácia ao documento ERRADO.
   */
  codigoValidacao?: string;
}

/** Monta o HTML do selo de assinatura digital ICP-Brasil. */
export function montarSeloAssinaturaDigital({
  nome,
  registro,
  data,
  compacto,
  codigoValidacao,
}: DadosSelo): string {
  const n = esc(nome);
  const r = esc(registro);
  const d = esc(data);

  if (compacto) {
    // B340 (AI_150) — com código, o QR entra à ESQUERDA, sob a marca ICP-Brasil
    // (mesma disposição da Memed). Sem código, mantém o selo original.
    const comQR = Boolean(codigoValidacao);
    // ⚠️ HTML em UMA LINHA só, sem indentação entre tags: a célula da LME
    // (.lme-fill) usa white-space:pre-wrap e renderizaria quebras/indentação como
    // espaço visível (selo esticado). white-space:normal blinda contra isso.
    return (
      `<div style="display:flex;align-items:center;gap:6px;line-height:1.2;text-align:left;white-space:normal;font-family:'Helvetica Neue',Arial,sans-serif;">` +
      `<div style="flex-shrink:0;text-align:center;">` +
      chaveSvg(18) +
      `<div style="font-size:6px;font-weight:700;color:#1a3a6e;letter-spacing:0.2px;">ICP-Brasil</div>` +
      (comQR
        ? `<div style="line-height:0;margin-top:2px;">${qrSvg(
            codigoValidacao as string,
            QR_COMPACTO_PX
          )}</div>`
        : "") +
      `</div>` +
      `<div style="font-size:8.4px;color:#1a1a1a;font-weight:400;">` +
      `<div style="font-weight:700;">${n}</div>` +
      `<div>${r}</div>` +
      `<div>${d}</div>` +
      (comQR
        ? `<div style="font-size:7px;color:#555;margin-top:2px;">Confira em hepato.app/validar</div>` +
          `<div style="font-size:7.6px;font-weight:700;color:#1a3a6e;letter-spacing:0.3px;">${esc(
            formatarCodigoExibicao(codigoValidacao as string)
          )}</div>`
        : "") +
      `</div>` +
      `</div>`
    );
  }

  return `<div style="display:inline-flex; align-items:center; gap:12px; border:1px solid #99a; border-radius:6px; padding:10px 14px; text-align:left; font-family:'Helvetica Neue',Arial,sans-serif;">
    <div style="flex-shrink:0; text-align:center;">
      ${chaveSvg(34)}
      <div style="font-size:9px; font-weight:700; color:#1a3a6e; letter-spacing:0.5px;">ICP-Brasil</div>
    </div>
    <div style="font-size:11px; color:#222; line-height:1.4;">
      <div style="font-weight:700;">Assinado digitalmente por ${n}</div>
      <div>${r} · ${d}</div>
      <div style="font-size:9px; color:#555;">Assinatura eletrônica qualificada (ICP-Brasil) — MP 2.200-2/2001 e Lei 14.063/2020. Valide em validar.iti.gov.br</div>
    </div>
  </div>`;
}

/** Data/hora "agora" no formato dd/mm/aaaa hh:mm (horário local). */
export function dataHoraSeloAgora(): string {
  const dt = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()} ${p(
    dt.getHours()
  )}:${p(dt.getMinutes())}`;
}
